import Reveal from "@/components/base/Reveal";

interface CurriculumItem {
  title: string;
  desc: string;
}

interface LevelSectionProps {
  level: {
    id: string;
    tag: string;
    name: string;
    headline: string;
    target: string;
    desc: string;
    curriculum: CurriculumItem[];
    features: string[];
    image: string;
  };
  dark?: boolean;
  flip?: boolean;
}

export default function LevelSection({ level, dark = false, flip = false }: LevelSectionProps) {
  return (
    <section
      id={level.id}
      className={`relative scroll-mt-32 overflow-hidden py-20 md:py-28 ${
        dark ? "bg-foreground-950" : "bg-background-50"
      }`}
    >
      {dark && <div className="pointer-events-none absolute inset-0 grain-light opacity-30" />}

      <div className="relative mx-auto max-w-content px-5 md:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal className={flip ? "lg:order-2" : ""}>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl">
              <img
                src={level.image}
                alt={`${level.name} 수업 이미지`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </Reveal>

          <Reveal delay={100} className={flip ? "lg:order-1" : ""}>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 font-label text-[10px] tracking-[0.2em] ${
                dark
                  ? "bg-white/5 text-primary-400 ring-1 ring-white/15"
                  : "bg-primary-100 text-primary-700"
              }`}
            >
              {level.tag}
            </span>
            <h2
              className={`mt-4 font-heading text-3xl font-black tracking-tight md:text-4xl ${
                dark ? "text-white" : "text-foreground-950"
              }`}
            >
              {level.name}
            </h2>
            <p
              className={`mt-2 font-heading text-lg font-bold ${
                dark ? "text-primary-500" : "text-primary-600"
              }`}
            >
              {level.headline}
            </p>
            <p
              className={`mt-3 text-sm leading-relaxed md:text-base ${
                dark ? "text-white/60" : "text-foreground-700"
              }`}
            >
              {level.desc}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-foreground-950">
                {level.target}
              </span>
              {level.features.map((f) => (
                <span
                  key={f}
                  className={`rounded-full px-3 py-1 text-xs ${
                    dark
                      ? "border border-white/15 text-white/70"
                      : "border border-background-200 bg-background-100 text-foreground-700"
                  }`}
                >
                  {f}
                </span>
              ))}
            </div>

            <div className="mt-7 space-y-4">
              {level.curriculum.map((c) => (
                <div
                  key={c.title}
                  className={`flex gap-4 rounded-xl p-4 ${
                    dark ? "bg-white/[0.04]" : "bg-background-100"
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                      dark
                        ? "bg-primary-500 text-foreground-950"
                        : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    <i className="ri-check-line" />
                  </span>
                  <div>
                    <h3
                      className={`font-heading text-sm font-bold ${
                        dark ? "text-white" : "text-foreground-950"
                      }`}
                    >
                      {c.title}
                    </h3>
                    <p className={`mt-0.5 text-sm ${dark ? "text-white/60" : "text-foreground-700"}`}>
                      {c.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}